import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useLanguage } from "@/i18n/LanguageProvider";
import LanguageSelector from "@/components/LanguageSelector";
import { languageDef, type Lang } from "@/i18n/locale";
import { analytics } from "@/lib/analytics";
import Footer from "@/components/layout/Footer";
import vyvIcon from "@/assets/vyv-icon.png";

const Welcome = () => {
  const navigate = useNavigate();
  const { lang, setLang, t } = useLanguage();
  const [showCta, setShowCta] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  // Reveal the CTA after the intro settles.
  useEffect(() => {
    const ctaTimer = setTimeout(() => setShowCta(true), 1500);
    return () => clearTimeout(ctaTimer);
  }, []);

  // Total lookup — `lang` is a closed union, so there is no miss to fall back
  // from. The old `?? LANG_OPTIONS[1]` made PT-BR the default by list POSITION,
  // which would have silently changed the moment a language was inserted above
  // it (Landing had the same line pointing at index 0 — two screens, two
  // different accidental defaults).
  const current = languageDef(lang);

  const chooseLang = (code: Lang) => {
    analytics.languageSelected(code);
    // Marks that the user has explicitly picked a language on the public
    // landing. Entry routing no longer depends on this flag (EntryGate uses the
    // Supabase session + vyv_has_authed), but we persist it for analytics/UX.
    localStorage.setItem("vyv_language_selected_v2", "true");
    setLangMenuOpen(false);
  };

  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center overflow-hidden"
      style={{ background: "#050810" }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5, ease: "easeOut" }}
    >
      {/* Language selector — top-right */}
      <div className="absolute top-0 right-0 z-20 pt-[max(env(safe-area-inset-top),16px)] pr-4">
        <button
          type="button"
          aria-label={t("welcome.languageSelector")}
          aria-haspopup="menu"
          aria-expanded={langMenuOpen}
          onClick={() => setLangMenuOpen((o) => !o)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-display"
          style={{ background: "hsl(222 18% 12% / 0.8)", border: "1px solid hsl(222 18% 22%)", color: "rgba(255,255,255,0.75)" }}
        >
          <span className="text-[14px] leading-none">{current.flag}</span>
          <span className="tracking-wide">{current.short}</span>
        </button>

        <AnimatePresence>
          {langMenuOpen && (
            <motion.div
              role="menu"
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.18 }}
              className="absolute right-4 mt-2 w-44 rounded-2xl overflow-hidden"
              style={{ background: "hsl(222 18% 10%)", border: "1px solid hsl(222 18% 20%)", boxShadow: "0 10px 30px rgba(0,0,0,0.45)" }}
            >
              <LanguageSelector variant="flag-menu" onSelect={chooseLang} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Ambient glow */}
      <div
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, hsl(187 72% 50% / 0.07) 0%, transparent 70%)" }}
      />

      {/* Centered content group */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 text-center">
        {/* Logo */}
        <motion.div
          className="flex flex-col items-center gap-6 mb-8"
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Logo icon with breathing glow */}
          <div className="relative flex items-center justify-center">
            {/* Primary cyan halo */}
            <motion.div
              className="absolute rounded-full pointer-events-none"
              style={{
                width: 280,
                height: 280,
                background: "radial-gradient(circle, hsl(187 72% 50% / 0.22) 0%, hsl(187 72% 50% / 0.08) 40%, transparent 70%)",
                filter: "blur(25px)",
              }}
              animate={{
                scale: [1, 1.3, 1],
                opacity: [0.6, 1, 0.6],
              }}
              transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
            />
            {/* Secondary wider halo */}
            <motion.div
              className="absolute rounded-full pointer-events-none"
              style={{
                width: 480,
                height: 480,
                background: "radial-gradient(circle, hsl(187 72% 50% / 0.06) 0%, transparent 65%)",
                filter: "blur(40px)",
              }}
              animate={{
                scale: [1, 1.15, 1],
                opacity: [0.4, 0.85, 0.4],
              }}
              transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
            />
            {/* Logo icon with pulsing drop-shadow */}
            <motion.img
              src={vyvIcon}
              alt="VYV"
              className="w-[120px] h-[120px] object-contain relative z-10"
              style={{ filter: "drop-shadow(0 0 20px hsl(187 72% 50% / 0.55)) drop-shadow(0 0 40px hsl(187 72% 50% / 0.25))" }}
              animate={{
                filter: [
                  "drop-shadow(0 0 20px hsl(187 72% 50% / 0.45)) drop-shadow(0 0 40px hsl(187 72% 50% / 0.20))",
                  "drop-shadow(0 0 28px hsl(187 72% 50% / 0.70)) drop-shadow(0 0 55px hsl(187 72% 50% / 0.30))",
                  "drop-shadow(0 0 20px hsl(187 72% 50% / 0.45)) drop-shadow(0 0 40px hsl(187 72% 50% / 0.20))",
                ],
              }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut", delay: 1 }}
            />
          </div>
          <h1 className="font-display font-bold tracking-[0.65em] pl-[0.65em] text-5xl text-gradient">VYV</h1>
        </motion.div>

        {/* Headline */}
        <motion.p
          className="font-display text-base text-muted-foreground/60 tracking-wide text-center mb-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
        >
          {t("welcome.tagline")}
        </motion.p>

        {/* Single poetic sub-line (condensed from the former 3 lines) */}
        <motion.p
          className="text-foreground/90 text-[15px] leading-relaxed text-center font-light max-w-[320px]"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        >
          {t("welcome.subline")}
        </motion.p>
      </div>

      {/* CTA — in normal flow at the bottom of the flex column. Previously
          `fixed bottom-0`, which took it out of flow so it overlapped the
          vertically-centered content on taller aspect ratios (iPad — Apple
          G4 rejection: "Begin" sat over the subline). As a flex child the
          flex-1 content above reserves its own space, so they never overlap. */}
      <div className="w-full shrink-0 px-6 pb-14 pointer-events-none" style={{ background: "linear-gradient(transparent, #050810 40%)" }}>
        <AnimatePresence>
          {showCta && (
            <motion.button
              className="w-full py-4 rounded-2xl font-display font-semibold text-base tracking-wide pointer-events-auto"
              style={{ background: "hsl(var(--primary))", color: "hsl(var(--background))" }}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => navigate("/auth")}
            >
              {t("welcome.cta")}
            </motion.button>
          )}
        </AnimatePresence>
        <motion.div
          className="pointer-events-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8, duration: 0.5 }}
        >
          <Footer variant="minimal" className="px-0 py-4 border-0" />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Welcome;
